import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  MapPin, 
  TrendingUp, 
  Cloud, 
  Wheat, 
  Calendar,
  BarChart3,
  Table,
  Target,
  RefreshCw
} from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import NotificationCenter from './NotificationCenter';
import { useApi, useAsyncApi } from '../hooks/useApi';
import { analyticsAPI, autoLogin } from '../services/api';
import { useToast } from '../hooks/use-toast';

// Import existing components
import WeatherMap from './WeatherMap';
import YieldChart from './YieldChart';
import WeatherChart from './WeatherChart';
import PredictionPanel from './PredictionPanel';
import DataTable from './DataTable';
import CorrelationChart from './CorrelationChart';

const RealDataDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const { toast } = useToast();
  const { execute: executeAsync, loading: asyncLoading } = useAsyncApi();
  
  // Auto-login on component mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await autoLogin();
      } catch (error) {
        toast({
          title: "Authentication Error",
          description: "Please check your connection and try again",
          variant: "destructive",
        });
      }
    };
    
    initializeAuth();
  }, [toast]);
  
  // Fetch summary data
  const { 
    data: summaryData, 
    loading: summaryLoading, 
    error: summaryError,
    refetch: refetchSummary 
  } = useApi(
    () => analyticsAPI.getSummary(),
    [],
    { showErrorToast: false }
  );
  
  const refreshData = async () => {
    await executeAsync(
      async () => {
        await refetchSummary();
        return "Data refreshed successfully";
      },
      { successMessage: "Dashboard data updated" }
    );
  };
  
  const getStatIcon = (type) => {
    switch(type) {
      case 'production': return <Wheat className="h-5 w-5 text-green-600" />;
      case 'yield': return <TrendingUp className="h-5 w-5 text-blue-600" />;
      case 'area': return <MapPin className="h-5 w-5 text-purple-600" />;
      case 'weather': return <Cloud className="h-5 w-5 text-orange-600" />;
      default: return null;
    }
  };
  
  const getRiskColor = (risk) => {
    switch(risk) {
      case 'Low': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'High': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };
  
  const formatNumber = (num, decimals = 1) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(decimals)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(decimals)}K`;
    }
    return num?.toFixed(decimals) || '0';
  };
  
  // Default values for loading state
  const defaultSummary = {
    total_production_tonnes: 0,
    average_yield: 0,
    total_area_hectares: 0,
    weather_risk: 'Low',
    year: new Date().getFullYear() - 1
  };
  
  const summary = summaryData || defaultSummary;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Agricultural Analytics Dashboard
              </h1>
              <p className="text-gray-600 dark:text-gray-300 mt-2">
                Comprehensive crop yield and weather analysis for Canadian agriculture
              </p>
              {summaryError && (
                <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
                  ⚠️ Using cached data - connection issues detected
                </p>
              )}
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Data: {summary.year}
                </span>
              </div>
              <Button
                onClick={refreshData}
                disabled={asyncLoading || summaryLoading}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${asyncLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <div className="flex items-center gap-2">
                <NotificationCenter />
                <ThemeToggle />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-all duration-300 border-gray-200 dark:border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Total Production</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {summaryLoading ? '...' : `${formatNumber(summary.total_production_tonnes)} t`}
                  </p>
                </div>
                {getStatIcon('production')}
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-all duration-300 border-gray-200 dark:border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Average Yield</p>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {summaryLoading ? '...' : `${summary.average_yield} t/ha`}
                  </p>
                </div>
                {getStatIcon('yield')}
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-all duration-300 border-gray-200 dark:border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Total Area</p>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {summaryLoading ? '...' : `${formatNumber(summary.total_area_hectares)} ha`}
                  </p>
                </div>
                {getStatIcon('area')}
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-all duration-300 border-gray-200 dark:border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Weather Risk</p>
                  <Badge className={getRiskColor(summary.weather_risk)}>
                    {summaryLoading ? '...' : summary.weather_risk}
                  </Badge>
                </div>
                {getStatIcon('weather')}
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6 bg-white dark:bg-gray-800 shadow-sm border-gray-200 dark:border-gray-700">
            <TabsTrigger value="overview" className="flex items-center gap-2 data-[state=active]:bg-gray-100 dark:data-[state=active]:bg-gray-700">
              <MapPin className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="weather" className="flex items-center gap-2 data-[state=active]:bg-gray-100 dark:data-[state=active]:bg-gray-700">
              <Cloud className="h-4 w-4" />
              Weather
            </TabsTrigger>
            <TabsTrigger value="yields" className="flex items-center gap-2 data-[state=active]:bg-gray-100 dark:data-[state=active]:bg-gray-700">
              <TrendingUp className="h-4 w-4" />
              Yields
            </TabsTrigger>
            <TabsTrigger value="predictions" className="flex items-center gap-2 data-[state=active]:bg-gray-100 dark:data-[state=active]:bg-gray-700">
              <Target className="h-4 w-4" />
              Predictions
            </TabsTrigger>
            <TabsTrigger value="correlations" className="flex items-center gap-2 data-[state=active]:bg-gray-100 dark:data-[state=active]:bg-gray-700">
              <BarChart3 className="h-4 w-4" />
              Analysis
            </TabsTrigger>
            <TabsTrigger value="data" className="flex items-center gap-2 data-[state=active]:bg-gray-100 dark:data-[state=active]:bg-gray-700">
              <Table className="h-4 w-4" />
              Data
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="lg:col-span-2">
                <WeatherMap />
              </div>
              <YieldChart />
              <WeatherChart />
            </div>
          </TabsContent>
          
          <TabsContent value="weather" className="mt-6">
            <div className="grid grid-cols-1 gap-6">
              <WeatherChart />
              <WeatherMap />
            </div>
          </TabsContent>
          
          <TabsContent value="yields" className="mt-6">
            <div className="grid grid-cols-1 gap-6">
              <YieldChart />
              <CorrelationChart />
            </div>
          </TabsContent>
          
          <TabsContent value="predictions" className="mt-6">
            <PredictionPanel />
          </TabsContent>
          
          <TabsContent value="correlations" className="mt-6">
            <div className="grid grid-cols-1 gap-6">
              <CorrelationChart />
            </div>
          </TabsContent>
          
          <TabsContent value="data" className="mt-6">
            <DataTable />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default RealDataDashboard;